import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';

import { toggleModalWithTimer, toggleSpinner } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { addExtras } from 'apps/auth/actions/extraAction';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import ChangePinLinkField from 'apps/auth/components/ga/cards/details/ChangePinLinkField';
import ModalWithTimer from 'apps/auth/components/utils/ModalWithTimer';
import SupportPopup from 'apps/auth/components/utils/popup/SupportPopup';
import { CANCEL_OR_REISSUE_CARD_POPUP, REISSUE_CARD_POPUP, SUPPORT_POPUP } from 'apps/auth/constants/ComponentNames';
import { AUTH_GA_CARD, AUTH_GA_STUB_PAGE, AUTH_GA_VIRTUAL_CARD_CREATE_PREVIEW } from 'apps/auth/constants/UrlConstants';
import { useExtra, usePageMessages } from 'apps/auth/hooks/selectors';
import { requestsReissueCard, SUCCESS } from 'apps/auth/services/GaCardsService';

import CancelOrReissueCardPopup from './CancelOrReissueCardPopup';
import LinkField from './LinkField';

const LinksForCardDetails = (props) => {
    const { card = {}, hasVirtualCard } = useExtra();
    const messages = usePageMessages();
    const popupRef = useRef(null);
    const [screen, setScreen] = useState('');
    const [popups, setPopups] = useState('');

    const onClickSuccess = () => {
        window.location.href = AUTH_GA_CARD + card?.id;
    };

    const getPopupComponent = () => {
        if (popups === CANCEL_OR_REISSUE_CARD_POPUP) {
            return <CancelOrReissueCardPopup CancelOrReissueCardPopup popupRef={popupRef} />;
        }
        if (popups === SUPPORT_POPUP) {
            return <SupportPopup popupRef={popupRef} />;
        }
        if (popups === REISSUE_CARD_POPUP) {
            return (
                <SimplePopup text={<DangerouslyHtml dangerouslyHtml={messages.reissueCardInfo} />} popupRef={popupRef}>
                    <PopupButton
                        title={messages.gotIt}
                        iconName="check"
                        text={messages.gotIt}
                        onClick={() => {
                            popupRef.current?.close();
                            requestsReissueCard(card, props.toggleSpinner, props.addExtras, setScreen);
                        }}
                    />
                </SimplePopup>
            );
        }
    };

    const onClickHandlerExpiredCard = (e) => {
        e.preventDefault();
        if (card.plastic) {
            popupRef.current?.open(<CancelOrReissueCardPopup agree={true} popupRef={popupRef} />);
        } else {
            setPopups(REISSUE_CARD_POPUP);
            popupRef?.current?.open();
        }
    };

    const getLinksForCardDetails = () => {
        if (card.frozen) {
            return (
                <>
                    {card.frozenByUser && (
                        <>
                            <LinkField
                                type="button"
                                onClick={(e) => {
                                    props.doRequest(e, `${AUTH_GA_CARD}${card.id}/unfreeze`, messages.messageCardUnfrozen);
                                }}
                                emphasizedtext
                                icon="snowflake-crossed"
                                text={messages.unfreezeCard}
                            />
                            <LinkField
                                type="button"
                                onClick={() => {
                                    setPopups(CANCEL_OR_REISSUE_CARD_POPUP);
                                    popupRef?.current?.open();
                                }}
                                emphasizedtext
                                icon="scissors"
                                text={messages.cancelCard}
                            />
                        </>
                    )}
                    {card.frozenBySystem && (
                        <LinkField
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                setPopups(SUPPORT_POPUP);
                                popupRef?.current?.open();
                            }}
                            emphasizedtext
                            icon="message-bubble"
                            text={messages.messageCardContactSupport}
                        />
                    )}
                </>
            );
        }

        if (card.expired) {
            return (
                <>
                    {card.plastic && !hasVirtualCard && (
                        <LinkField type="reactLink" to={AUTH_GA_VIRTUAL_CARD_CREATE_PREVIEW} emphasizedtext icon="card-plus" text={messages.getVirtualCard} />
                    )}
                    {(card.virtual || (card.plastic && hasVirtualCard)) && (
                        <LinkField
                            type="reactLink"
                            to={AUTH_GA_STUB_PAGE}
                            onClick={onClickHandlerExpiredCard}
                            emphasizedtext
                            icon={card?.plastic ? 'scissors' : 'arrow-exchange'}
                            text={card?.plastic ? messages.cancelPlasticCardButton : messages.reopenExpiredCardButton}
                        />
                    )}
                    {card.plastic && (
                        <LinkField
                            onClick={(e) => {
                                e.preventDefault();
                                popupRef?.current?.close();
                                props.toggleChat(true);
                            }}
                            emphasizedtext
                            icon="lifebuoy"
                            text={messages.morePopupChatTitle}
                        />
                    )}
                </>
            );
        }

        return (
            <>
                {card.expiredSoon && card.virtual && (
                    <LinkField
                        type="reactLink"
                        to={AUTH_GA_STUB_PAGE}
                        onClick={onClickHandlerExpiredCard}
                        emphasizedtext
                        icon="arrow-exchange"
                        text={messages.reopenExpiredCardButton}
                    />
                )}
                {card.expiredSoon && card.plastic && !hasVirtualCard && (
                    <LinkField type="reactLink" to={AUTH_GA_VIRTUAL_CARD_CREATE_PREVIEW} emphasizedtext icon="card-plus" text={messages.getVirtualCard} />
                )}
                <LinkField
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        const message = props.showMode ? messages.messageCardDetailsHidden : messages.messageCardDetailsShown;
                        props.toggleModalWithTimer(true, message, () => {
                            props.toggleModalWithTimer(false, '', false).then(() => props.setShowMode(!props.showMode));
                        });
                    }}
                    emphasizedtext
                    icon={props.showMode ? 'eye-crossed' : 'eye'}
                    text={props.showMode ? messages.cardHideDetails : messages.viewCardDetails}
                />

                {card.plastic && <ChangePinLinkField setShowModal={props.setShowModal} />}

                <LinkField
                    type="button"
                    onClick={(e) => {
                        props.doRequest(e, `${AUTH_GA_CARD}${card.id}/freeze`, messages.messageCardFrozen);
                    }}
                    emphasizedtext
                    icon="snowflake"
                    text={messages.freezeCard}
                />
            </>
        );
    };
    if (screen === SUCCESS) {
        return <ModalWithTimer text={messages.messageCardActivated} onClick={onClickSuccess} />;
    }

    return (
        <>
            {getLinksForCardDetails()}
            <Separator />
            {card.plastic && (
                <LinkField type="reactLink" to={`${AUTH_GA_CARD}${card.id}/atm-limits`} negativeMargin icon="cash-stack" text={messages.atmLimits} />
            )}
            <LinkField type="reactLink" to={`${AUTH_GA_CARD}${card.id}/activity`} negativeMargin icon="list-marked" text={messages.activity} />
            <PopupNew ref={popupRef} getPopupComponent={getPopupComponent} />
        </>
    );
};

export default connect(null, { toggleSpinner, toggleModalWithTimer, addExtras, toggleChat })(LinksForCardDetails);
